import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Heart, Sparkles, Leaf, Hand } from "lucide-react";
import { BRAND_COPY, PROCESS_STEPS } from "../mock";

const VALUES = [
  {
    Icon: Leaf,
    title: "Naturally Sourced",
    body: "Every jar is built on 100% pure and natural ingredients — raw shea, cold-pressed carriers, no fillers."
  },
  {
    Icon: Hand,
    title: "Hand-Whipped",
    body: "Micro-batched by hand in the AJ kitchen. Whipped cold, rested, and re-whipped for that mousse lift."
  },
  {
    Icon: Heart,
    title: "For Every Skin",
    body: "Formulated for Her and For Him — with unscented and 20+ inspired fragrances so there's a match for everyone."
  },
  {
    Icon: Sparkles,
    title: "Rooted in Ritual",
    body: "Self-care as a delightful, everyday practice. Butter, oil, spray — a system, not a shelf-filler."
  }
];

const About = () => {
  return (
    <main className="min-h-screen bg-charcoal">
      {/* Hero */}
      <section className="relative overflow-hidden pt-6">
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute -right-24 top-1/3 w-[600px] h-[600px] rounded-full opacity-[0.14] blur-3xl"
            style={{ background: "radial-gradient(circle, #D89999 0%, transparent 60%)" }}
          />
        </div>

        <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-14 pb-10 relative">
          <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-off-white/50 mb-8">
            <Link to="/" className="hover:text-rose">HOME</Link>
            <span>/</span>
            <span className="text-rose">ABOUT ME</span>
          </div>

          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-4">// 00 — FOUNDER'S NOTE</p>
              <img
                src="https://customer-assets-m6fa6gv7.emergentagent.net/job_neon-workshop-3/artifacts/8muchqej_whip_it_up.png"
                alt="Whip it Up"
                className="h-16 md:h-24 w-auto object-contain mb-2"
              />
              <h1 className="font-display text-6xl md:text-8xl lg:text-9xl uppercase leading-[0.86] tracking-tighter text-off-white">
                Meet <span className="text-rose">AJ.</span>
              </h1>
              <p className="mt-6 max-w-2xl text-off-white/70 text-lg md:text-xl font-light leading-relaxed">
                Founder of Whip It Up. Chemist of the whipped-butter kitchen. Believer
                that self-care should be a delightful experience — not a chore.
              </p>
            </div>

            <div className="md:col-span-4">
              <div className="border border-off-white/10 p-6 bg-off-white/[0.02]">
                <p className="font-mono text-[10px] tracking-[0.3em] text-rose">// AT A GLANCE</p>
                <ul className="mt-5 space-y-3">
                  {[
                    ["Founder", BRAND_COPY.aboutUs.founder],
                    ["Established", "2023"],
                    ["Batch Size", "< 40 units"],
                    ["Ship Window", "48 hours"],
                    ["Returns", "None — just love"]
                  ].map(([k, v]) => (
                    <li key={k} className="flex justify-between items-baseline border-b border-off-white/10 pb-2">
                      <span className="font-mono text-[10px] tracking-[0.25em] text-off-white/50 uppercase">{k}</span>
                      <span className="text-sm text-off-white text-right">{v}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="border-t border-off-white/10 py-20 md:py-28">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-10 md:gap-16">
            <div className="md:col-span-5">
              <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-3">// 01 — THE STORY</p>
              <h2 className="font-display text-5xl md:text-6xl uppercase leading-[0.9] tracking-tighter text-off-white">
                Self-care<br />meets luxury.
              </h2>
              <div className="mt-8 border-l-2 border-rose pl-5 py-2">
                <p className="font-serif-display italic text-xl md:text-2xl text-off-white/90 leading-snug">
                  “{BRAND_COPY.favoriteSayings[0]}.”
                </p>
                <p className="font-mono text-[10px] tracking-[0.3em] text-rose mt-3">// AJ'S SAYING</p>
              </div>
            </div>

            <div className="md:col-span-7 space-y-6 text-off-white/80 font-light leading-relaxed">
              {BRAND_COPY.aboutUs.paragraphs.map((p, i) => (
                <p key={i} className={i === 0 ? "text-lg text-off-white" : "text-[15px]"}>
                  {p}
                </p>
              ))}
              <p className="font-serif-display italic text-rose text-xl md:text-2xl pt-4">
                “{BRAND_COPY.favoriteSayings[1]}.”
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="border-t border-off-white/10 py-20 md:py-28 bg-charcoal-2">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <div className="mb-14 grid md:grid-cols-12 gap-6 items-end">
            <div className="md:col-span-7">
              <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-3">// 02 — WHAT WE STAND FOR</p>
              <h2 className="font-display text-5xl md:text-6xl uppercase leading-[0.9] tracking-tighter text-off-white">
                Four principles.<br />No compromise.
              </h2>
            </div>
            <p className="md:col-span-5 text-off-white/60 font-light leading-relaxed">
              Every product carries these four commitments. If a formula, ingredient,
              or shortcut would break any of them — it doesn't make the cut.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {VALUES.map(({ Icon, title, body }, i) => (
              <div key={title} className="border border-off-white/10 hover:border-rose transition-colors-only p-6 bg-off-white/[0.02] flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <span className="w-10 h-10 border border-rose/60 flex items-center justify-center">
                    <Icon size={18} className="text-rose" strokeWidth={1.5} />
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.3em] text-off-white/40">0{i + 1}</span>
                </div>
                <h3 className="font-display text-xl uppercase text-off-white leading-tight">{title}</h3>
                <p className="text-off-white/70 text-sm font-light leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process nod */}
      <section className="border-t border-off-white/10 py-20 md:py-28">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-6">
              <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-3">// 03 — HOW I MAKE IT</p>
              <h2 className="font-display text-5xl md:text-6xl uppercase leading-[0.9] tracking-tighter text-off-white">
                A three-station<br />kitchen.
              </h2>
            </div>
            <p className="md:col-span-5 md:col-start-8 text-off-white/60 font-light text-lg leading-relaxed self-end">
              Every jar walks the same line. No shortcuts, no black-box — you see
              every step from spec to finished product.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {PROCESS_STEPS.map((s) => (
              <div key={s.number} className="border border-off-white/10 p-6 bg-off-white/[0.02]">
                <div className="flex items-baseline justify-between mb-4">
                  <span className="font-mono text-[10px] tracking-[0.3em] text-rose">// STATION</span>
                  <span className="font-display text-5xl text-off-white/15">{s.number}</span>
                </div>
                <h3 className="font-display text-2xl uppercase text-off-white">{s.title}</h3>
                <p className="font-serif-display italic text-rose mt-1">{s.subtitle}</p>
                <p className="mt-4 text-off-white/70 text-sm font-light leading-relaxed">{s.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Return policy + CTA */}
      <section className="border-t border-off-white/10 py-20 md:py-28 bg-charcoal-2">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7">
              <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-3">// 04 — HOUSE POLICY</p>
              <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.95] tracking-tighter text-off-white">
                {BRAND_COPY.returnPolicy.title}.
              </h2>
              <p className="mt-6 max-w-xl text-off-white/70 text-lg font-light leading-relaxed">
                {BRAND_COPY.returnPolicy.body}
              </p>
            </div>
            <div className="md:col-span-5 flex md:justify-end">
              <Link
                to="/shop"
                className="btn-blueprint inline-flex items-center gap-3 h-14 px-8 bg-rose text-charcoal font-mono text-[12px] tracking-[0.25em] font-semibold hover:bg-off-white transition-colors-only"
              >
                SHOP THE LINE <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
